import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Warning } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export const LowStockReport = () => {
  const [slabs, setSlabs] = useState([]);
  const [sinks, setSinks] = useState([]);
  const [threshold, setThreshold] = useState(2);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      api.get("/slabs/low-stock", { params: { threshold } }),
      api.get("/sinks/low-stock"),
    ]);
    setSlabs(a.data.items);
    setSinks(b.data.items);
  }, [threshold]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div
      className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-5"
      data-testid="low-stock-report"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Warning size={16} weight="bold" className="text-amber-400" />
          <div className="text-xs uppercase tracking-widest text-zinc-500">
            Low stock ({slabs.length} color{slabs.length === 1 ? "" : "s"} ·{" "}
            {sinks.length} sink
            {sinks.length === 1 ? "" : "s"})
          </div>
        </div>
        <div className="flex gap-2">
          {[1, 2, 3, 5].map((t) => (
            <button
              key={t}
              data-testid={`low-stock-threshold-${t}`}
              onClick={() => setThreshold(t)}
              className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-widest transition-colors duration-150 ${
                threshold === t
                  ? "border-indigo-400/50 bg-indigo-500/10 text-indigo-300"
                  : "border-zinc-800 bg-zinc-950 text-zinc-500 hover:text-zinc-300"
              }`}
            >
              ≤ {t}
            </button>
          ))}
        </div>
      </div>

      {slabs.length === 0 && sinks.length === 0 ? (
        <p className="mt-3 text-sm text-zinc-500">
          Nothing at or below {threshold} on hand — stock levels look healthy.
        </p>
      ) : (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-600">
              Slab colors
            </div>
            <div className="mt-2 space-y-2">
              {slabs.length === 0 && (
                <p className="text-xs text-zinc-500">All colors stocked.</p>
              )}
              {slabs.map((s) => (
                <div
                  key={s.slab_color}
                  data-testid={`report-low-slab-${s.slab_color}`}
                  className="flex items-center justify-between gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3"
                >
                  <Link
                    to="/inventory/slabs"
                    className="min-w-0 flex-1 hover:opacity-80"
                  >
                    <span className="block truncate text-sm font-semibold">
                      {s.slab_color}
                    </span>
                    <span className="font-mono-data block text-xs text-amber-400">
                      {s.count} left · {s.sqft} ft²
                    </span>
                  </Link>
                  <Link
                    to={`/inventory/purchase-orders/new?slab_color=${encodeURIComponent(s.slab_color)}`}
                    data-testid={`report-reorder-slab-${s.slab_color}`}
                    className="shrink-0 rounded-full border border-indigo-400/40 bg-indigo-500/10 px-3 py-1 text-[10px] uppercase tracking-widest text-indigo-300 hover:bg-indigo-500/20"
                  >
                    Reorder
                  </Link>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-600">
              Sinks
            </div>
            <div className="mt-2 space-y-2">
              {sinks.length === 0 && (
                <p className="text-xs text-zinc-500">All sinks stocked.</p>
              )}
              {sinks.map((s) => (
                <div
                  key={s.id}
                  data-testid={`report-low-sink-${s.id}`}
                  className="flex items-center justify-between gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3"
                >
                  <Link
                    to={`/inventory/sinks/${s.id}`}
                    className="min-w-0 flex-1 hover:opacity-80"
                  >
                    <span className="block truncate text-sm font-semibold">
                      {s.label}
                    </span>
                    <span className="font-mono-data block text-xs text-amber-400">
                      {s.quantity} left
                    </span>
                  </Link>
                  <Link
                    to={`/inventory/purchase-orders/new?sink_key=${s.id}`}
                    data-testid={`report-reorder-sink-${s.id}`}
                    className="shrink-0 rounded-full border border-indigo-400/40 bg-indigo-500/10 px-3 py-1 text-[10px] uppercase tracking-widest text-indigo-300 hover:bg-indigo-500/20"
                  >
                    Reorder
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
